import { useQuery } from 'react-query'
import { AppointmentNoteAddenda as AddendaRoute } from '../api/routes'
import { QUERY_KEYS } from '../api/querykeys'
import { useAlertContext } from '../context/AlertContext'
import { useSourcePluginContext } from '../context/SourcePluginContext'
import { AppointmentNoteAddendum } from '../types/types'

/**
 * The note payload embeds raw addendum rows, which carry actorId but no name.
 * This route resolves the actor, so the history reads "Edited by <person>"
 * rather than a uuid.
 */
const useAppointmentNoteAddenda = (
  noteId?: string
): { addenda: AppointmentNoteAddendum[]; isLoading: boolean } => {
  const { setAlertText } = useAlertContext()
  const { getAuthHeader } = useSourcePluginContext()

  const fetchAddenda = async (): Promise<AppointmentNoteAddendum[]> => {
    if (!noteId) {
      return []
    }

    const authHeader = await getAuthHeader()

    return fetch(`${process.env.REACT_APP_SERVER_URL}${AddendaRoute(noteId)}`, {
      headers: authHeader,
    })
      .then(async (res) => {
        const data = await res.json()

        if (res.ok) {
          return data
        }

        setAlertText(`Error loading edit history (${res.statusText})`)
        return []
      })
      .catch(() => {
        setAlertText('Error loading edit history')
        return []
      })
  }

  const { data, isLoading } = useQuery(
    [QUERY_KEYS.APPOINTMENT_NOTE_ADDENDA, noteId ?? 'none'],
    fetchAddenda,
    { enabled: !!noteId }
  )

  return { addenda: data ?? [], isLoading }
}

export default useAppointmentNoteAddenda
